import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslation } from "i18n-fe";
import { useScoreApi, unwrap } from "../api/scoreApi";
import useVenueDateTime from "../hooks/useVenueDateTime";

// #3342 (scribe-resilience): a seat with no authoritative sheet whose only
// sheet(s) are tablet-RESENT draft contenders (#3334). The compare dialog
// needs an authoritative side, so this is the seat's one action: promote a
// resent sheet through the existing switch route (switchAuthoritativeScoreSheet,
// which promotes a lone resend, promoteResendAlone). Same canSwitch posture as
// ContenderCompareDialog: without the permission there is no button at all.

const CAPTURED_FORMAT = {
  year: "numeric",
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
  second: "2-digit",
};

export default function ResentSheetPromotePanel({ seat, label, canSwitch, onPromoted }) {
  const { t } = useTranslation("scoreadmin");
  const scoreApi = useScoreApi();
  const fmt = useVenueDateTime(CAPTURED_FORMAT);
  const [confirmSheetId, setConfirmSheetId] = useState(null);
  const [promoting, setPromoting] = useState(false);
  const [error, setError] = useState(null);

  async function handleConfirm() {
    if (!confirmSheetId) return;
    try {
      setPromoting(true);
      setError(null);
      const updated = unwrap(await scoreApi.scoreSheets.switchAuthoritativeScoreSheet(confirmSheetId));
      onPromoted?.(updated);
      setConfirmSheetId(null);
    } catch (err) {
      setError(err);
      setConfirmSheetId(null);
    } finally {
      setPromoting(false);
    }
  }

  return (
    <Alert severity="warning" sx={{ mt: 2 }} data-testid="resent-sheet-promote-panel">
      <Typography variant="subtitle2" data-testid="resent-sheet-promote-title">
        {t("contenderCompare.orphan.title", { judgeId: label })}
      </Typography>
      <Typography variant="body2">{t("contenderCompare.orphan.body")}</Typography>
      {error && (
        <Alert severity="error" sx={{ mt: 1 }} data-testid="resent-sheet-promote-error">
          {t("contenderCompare.orphan.error")}
        </Alert>
      )}
      <Stack spacing={1} sx={{ mt: 1 }}>
        {seat.sheets.map((sheet) => (
          <Box key={sheet.id} data-testid={`resent-sheet-row-${sheet.id}`}>
            <Typography variant="body2" color="text.secondary">
              {t("contenderCompare.capturedAtLabel")}{" "}
              {sheet.provenance?.capturedAt ? fmt(sheet.provenance.capturedAt) : "–"}
            </Typography>
            {canSwitch && (
              <Button
                size="small"
                variant="outlined"
                color="warning"
                onClick={() => setConfirmSheetId(sheet.id)}
                data-testid="resent-sheet-promote-button"
              >
                {t("contenderCompare.orphan.promoteButton")}
              </Button>
            )}
          </Box>
        ))}
      </Stack>

      <Dialog open={Boolean(confirmSheetId)} onClose={() => setConfirmSheetId(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t("contenderCompare.orphan.confirmTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t("contenderCompare.orphan.confirmBody", { judgeId: label })}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmSheetId(null)} disabled={promoting}>
            {t("contenderCompare.orphan.cancel")}
          </Button>
          <Button
            onClick={handleConfirm}
            loading={promoting}
            variant="contained"
            data-testid="resent-sheet-confirm-promote"
          >
            {t("contenderCompare.orphan.confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </Alert>
  );
}
